import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { adminCan, firstAllowedPath } from '../lib/permissions';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('adminToken'));
  const lastRefresh = useRef(0);

  const logout = useCallback(() => {
    localStorage.removeItem('adminToken');
    setToken(null);
    setAdmin(null);
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  const fetchCurrentAdmin = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setAdmin(response.data);
    } catch (error) {
      console.error('Failed to fetch admin:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token && !admin) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentAdmin();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  /**
   * Re-read the admin + live permissions from the server. Permissions are
   * resolved from the role on every request, so this keeps the menu and
   * buttons in step when a Super Admin edits the role. Throttled.
   */
  const refreshAccess = useCallback(async (force = false) => {
    if (!localStorage.getItem('adminToken')) return;
    const now = Date.now();
    if (!force && now - lastRefresh.current < 5000) return;
    lastRefresh.current = now;
    try {
      const response = await axios.get(`${API}/auth/me`);
      setAdmin(response.data);
    } catch (error) {
      if (error?.response?.status === 401 || error?.response?.status === 403) logout();
    }
  }, [logout]);

  // Refresh when the tab regains focus, and when the API reports a permission
  // problem (role changed since this page loaded).
  useEffect(() => {
    if (!admin) return undefined;
    const onFocus = () => refreshAccess();
    window.addEventListener('focus', onFocus);

    const interceptorId = axios.interceptors.response.use(
      (r) => r,
      (error) => {
        if (error?.response?.status === 403 && error.response.data?.code === 'FORBIDDEN') {
          refreshAccess();
        }
        return Promise.reject(error);
      }
    );
    return () => {
      window.removeEventListener('focus', onFocus);
      axios.interceptors.response.eject(interceptorId);
    };
  }, [admin?.id, refreshAccess]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, admin: adminData } = response.data;
    localStorage.setItem('adminToken', access_token);
    setToken(access_token);
    setAdmin(adminData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    return adminData;
  };

  /** can('orders', 'edit') — action defaults to 'view'. */
  const can = useCallback(
    (resource, action = 'view') => adminCan(admin, resource, action),
    [admin]
  );

  const value = {
    admin,
    login,
    logout,
    loading,
    isAuthenticated: !!admin,
    can,
    refreshAccess,
    isSuperAdmin: !!admin?.isSuperAdmin,
    dashboardScope: admin?.dashboardScope || 'all',
    homePath: firstAllowedPath(admin)
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
